import { useCallback, useEffect, useState } from 'react';
import { api, type ApiError } from '../../api/client';
import { Alert } from '../../components/Field';
import { titleCase } from '../../lib/format';

type Kind = 'banners' | 'announcements' | 'pages' | 'faqs' | 'featured';

interface ScheduledRow {
  id: string;
  status: string;
  live: boolean;
  window: string;
  startAt?: string | null;
  endAt?: string | null;
  [key: string]: unknown;
}

const KINDS: Array<{ key: Kind; label: string; blurb: string }> = [
  { key: 'banners', label: 'Banners', blurb: 'Homepage, promotional and course banners.' },
  { key: 'announcements', label: 'Announcements', blurb: 'Notices to everyone, a tenant or a role.' },
  { key: 'pages', label: 'Pages', blurb: 'Static pages with a slug and meta description.' },
  { key: 'faqs', label: 'FAQs', blurb: 'Questions, grouped by category.' },
  { key: 'featured', label: 'Featured', blurb: 'Curated courses and products per surface.' },
];

const BLANK: Record<Kind, Record<string, unknown>> = {
  banners: { type: 'HOMEPAGE', title: '', subtitle: '', linkUrl: '', linkLabel: '', status: 'DRAFT', displayOrder: 0 },
  announcements: { title: '', body: '', audienceType: 'ALL', audienceRole: '', scopeToMyTenant: false, status: 'DRAFT' },
  pages: { slug: '', title: '', body: '', metaTitle: '', metaDescription: '', status: 'DRAFT' },
  faqs: { category: '', question: '', answer: '', displayOrder: 0, status: 'DRAFT' },
  featured: { surface: 'HOMEPAGE', itemType: 'COURSE', itemId: '', displayOrder: 0, status: 'PUBLISHED' },
};

/**
 * Site content (BRD FR-CMS-001 to FR-CMS-005).
 *
 * Every list says whether each item is showing and why — "Scheduled for
 * 8 October", "Ended 15 August", "Draft" — because the commonest question an
 * editor has is why something they published is not on the site.
 */
export function SiteContent() {
  const [kind, setKind] = useState<Kind>('banners');
  const [rows, setRows] = useState<ScheduledRow[] | null>(null);
  const [draft, setDraft] = useState<Record<string, unknown> | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    setRows(null);
    api<{ items: ScheduledRow[] }>(`/admin/site/${kind}`)
      .then((result) => setRows(result.items))
      .catch((caught) => setError(caught as ApiError));
  }, [kind]);

  useEffect(load, [load]);

  function startNew() {
    setEditingId(null);
    setDraft({ ...BLANK[kind] });
    setError(null);
    setNotice(null);
  }

  async function startEdit(row: ScheduledRow) {
    setError(null);
    setNotice(null);
    setEditingId(row.id);
    if (kind === 'pages') {
      // The listing omits the body to keep it small; the editor needs it.
      try {
        const full = await api<Record<string, unknown>>(`/admin/site/pages/${row.id}`);
        setDraft(full);
        return;
      } catch (caught) {
        setError(caught as ApiError);
      }
    }
    setDraft({ ...row });
  }

  async function save() {
    if (!draft) return;
    setError(null);
    setNotice(null);

    // Strip the read-only fields the listing carries; the API rejects unknown
    // properties and they would otherwise be sent straight back.
    const body: Record<string, unknown> = { ...draft };
    for (const key of ['id', 'live', 'window', 'updatedAt', 'tenantId', 'targetStatus', 'title_']) {
      delete body[key];
    }
    for (const key of Object.keys(body)) {
      if (body[key] === '') body[key] = undefined;
    }
    // Title is required on most kinds, so an empty string must not be dropped.
    if ('title' in draft && draft.title !== undefined) body.title = draft.title;

    try {
      await api(`/admin/site/${kind}${editingId ? `/${editingId}` : ''}`, {
        method: editingId ? 'PUT' : 'POST',
        body: JSON.stringify(body),
      });
      setNotice(editingId ? 'Saved.' : 'Created.');
      setDraft(null);
      setEditingId(null);
      load();
    } catch (caught) {
      setError(caught as ApiError);
    }
  }

  async function remove(id: string) {
    setError(null);
    try {
      await api(`/admin/site/${kind}/${id}`, { method: 'DELETE' });
      setNotice('Removed.');
      load();
    } catch (caught) {
      setError(caught as ApiError);
    }
  }

  const active = KINDS.find((entry) => entry.key === kind)!;

  return (
    <>
      <div className="page__head">
        <h1 className="page__title">Site content</h1>
        <p className="page__lede">
          Everything the public site shows, with a schedule. An item appears and disappears exactly
          within its window — nothing needs to be remembered and taken down by hand.
        </p>
      </div>

      {error ? <Alert kind="error" title={error.message} items={error.details} /> : null}
      {notice ? <Alert kind="success" title={notice} /> : null}

      <div className="tabs" role="tablist">
        {KINDS.map((entry) => (
          <button
            key={entry.key}
            role="tab"
            aria-selected={kind === entry.key}
            className={`tabs__tab${kind === entry.key ? ' tabs__tab--on' : ''}`}
            onClick={() => {
              setKind(entry.key);
              setDraft(null);
              setEditingId(null);
            }}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <div className="card">
        <div className="card__head">
          <span className="card__title">
            {active.label} <span className="muted" style={{ fontWeight: 400 }}>— {active.blurb}</span>
          </span>
          <button className="btn btn--sm" onClick={startNew}>
            New {active.label.replace(/s$/, '').toLowerCase()}
          </button>
        </div>

        {!rows ? (
          <div className="spinner">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="empty">Nothing yet.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{kind === 'faqs' ? 'Question' : 'Title'}</th>
                <th style={{ width: 160 }}>{secondaryLabel(kind)}</th>
                <th style={{ width: 90 }}>Status</th>
                <th style={{ width: 200 }}>Showing</th>
                <th style={{ width: 130 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{String(row[primaryField(kind)] ?? '—')}</td>
                  <td className="muted">{String(row[secondaryField(kind)] ?? '—')}</td>
                  <td>
                    <span className={`pill pill--${row.status === 'PUBLISHED' ? 'ok' : 'quiet'}`}>
                      {titleCase(String(row.status))}
                    </span>
                  </td>
                  <td>
                    <span className={row.live ? 'live live--on' : 'live'}>
                      <span className="live__dot" aria-hidden="true" />
                      {row.window}
                    </span>
                  </td>
                  <td>
                    <button className="btn btn--quiet btn--sm" onClick={() => void startEdit(row)}>
                      Edit
                    </button>
                    <button
                      className="btn btn--quiet btn--sm"
                      onClick={() => void remove(row.id)}
                      style={{ marginLeft: 6 }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {draft ? (
        <div className="card" style={{ marginTop: 20 }}>
          <div className="card__head">
            <span className="card__title">
              {editingId ? 'Edit' : 'New'} {active.label.replace(/s$/, '').toLowerCase()}
            </span>
            <button className="btn btn--quiet btn--sm" onClick={() => setDraft(null)}>
              Cancel
            </button>
          </div>
          <div className="card__body stack">
            {fieldsFor(kind).map((field) => (
              <label className="field" key={field.name}>
                <span className="field__label">{field.label}</span>
                {field.options ? (
                  <select
                    className="field__input"
                    value={String(draft[field.name] ?? '')}
                    onChange={(event) =>
                      setDraft({ ...draft, [field.name]: event.target.value })
                    }
                  >
                    {field.options.map((option) => (
                      <option key={option} value={option}>
                        {titleCase(option)}
                      </option>
                    ))}
                  </select>
                ) : field.multiline ? (
                  <textarea
                    className="field__input"
                    rows={field.name === 'body' ? 12 : 4}
                    value={String(draft[field.name] ?? '')}
                    onChange={(event) => setDraft({ ...draft, [field.name]: event.target.value })}
                  />
                ) : field.type === 'checkbox' ? (
                  <input
                    type="checkbox"
                    checked={Boolean(draft[field.name])}
                    onChange={(event) => setDraft({ ...draft, [field.name]: event.target.checked })}
                  />
                ) : (
                  <input
                    className="field__input"
                    type={field.type ?? 'text'}
                    value={toInputValue(draft[field.name], field.type)}
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        [field.name]:
                          field.type === 'number'
                            ? Number(event.target.value)
                            : field.type === 'datetime-local'
                              ? event.target.value
                                ? new Date(event.target.value).toISOString()
                                : undefined
                              : event.target.value,
                      })
                    }
                  />
                )}
                {field.hint ? <span className="field__hint">{field.hint}</span> : null}
              </label>
            ))}

            <div className="row" style={{ gap: 8 }}>
              <button className="btn" onClick={() => void save()}>
                {editingId ? 'Save' : 'Create'}
              </button>
              <span className="muted" style={{ alignSelf: 'center' }}>
                A draft is never visible, whatever its window says.
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

interface FieldSpec {
  name: string;
  label: string;
  type?: string;
  multiline?: boolean;
  options?: string[];
  hint?: string;
}

const WINDOW_FIELDS: FieldSpec[] = [
  { name: 'startAt', label: 'Starts showing', type: 'datetime-local', hint: 'Leave empty for immediately.' },
  { name: 'endAt', label: 'Stops showing', type: 'datetime-local', hint: 'Leave empty for indefinitely.' },
  { name: 'displayOrder', label: 'Display order', type: 'number' },
  { name: 'status', label: 'Status', options: ['DRAFT', 'PUBLISHED', 'ARCHIVED'] },
];

function fieldsFor(kind: Kind): FieldSpec[] {
  switch (kind) {
    case 'banners':
      return [
        { name: 'type', label: 'Type', options: ['HOMEPAGE', 'PROMOTIONAL', 'COURSE'] },
        { name: 'title', label: 'Title' },
        { name: 'subtitle', label: 'Subtitle', multiline: true },
        { name: 'linkUrl', label: 'Link', hint: 'A path such as /catalog.' },
        { name: 'linkLabel', label: 'Link label' },
        ...WINDOW_FIELDS,
      ];
    case 'announcements':
      return [
        { name: 'title', label: 'Title' },
        { name: 'body', label: 'Body', multiline: true },
        { name: 'audienceType', label: 'Audience', options: ['ALL', 'TENANT', 'ROLE'] },
        { name: 'audienceRole', label: 'Role', hint: 'Only when the audience is a role, e.g. ORG_MANAGER.' },
        {
          name: 'scopeToMyTenant',
          label: 'Confine to my organization',
          type: 'checkbox',
          hint: 'Leave off for a platform-wide notice (needs permission to see all customers).',
        },
        ...WINDOW_FIELDS.filter((field) => field.name !== 'displayOrder'),
      ];
    case 'pages':
      return [
        { name: 'slug', label: 'Slug', hint: 'The address, e.g. refund-policy.' },
        { name: 'title', label: 'Title' },
        { name: 'body', label: 'Body', multiline: true },
        { name: 'metaTitle', label: 'Meta title' },
        { name: 'metaDescription', label: 'Meta description', multiline: true },
        { name: 'status', label: 'Status', options: ['DRAFT', 'PUBLISHED', 'ARCHIVED'] },
      ];
    case 'faqs':
      return [
        { name: 'category', label: 'Category' },
        { name: 'question', label: 'Question' },
        { name: 'answer', label: 'Answer', multiline: true },
        { name: 'displayOrder', label: 'Display order', type: 'number' },
        { name: 'status', label: 'Status', options: ['DRAFT', 'PUBLISHED', 'ARCHIVED'] },
      ];
    case 'featured':
      return [
        { name: 'surface', label: 'Surface', options: ['HOMEPAGE', 'CATALOG', 'CATEGORY'] },
        { name: 'itemType', label: 'What', options: ['COURSE', 'PRODUCT'] },
        { name: 'itemId', label: 'Identifier', hint: 'The course or product id.' },
        ...WINDOW_FIELDS,
      ];
  }
}

function primaryField(kind: Kind): string {
  return kind === 'faqs' ? 'question' : 'title';
}

function secondaryField(kind: Kind): string {
  if (kind === 'banners') return 'type';
  if (kind === 'announcements') return 'audienceType';
  if (kind === 'pages') return 'slug';
  if (kind === 'faqs') return 'category';
  return 'surface';
}

function secondaryLabel(kind: Kind): string {
  if (kind === 'banners') return 'Type';
  if (kind === 'announcements') return 'Audience';
  if (kind === 'pages') return 'Slug';
  if (kind === 'faqs') return 'Category';
  return 'Surface';
}

/** datetime-local wants `YYYY-MM-DDTHH:mm`, not an ISO string with a zone. */
function toInputValue(value: unknown, type?: string): string {
  if (value === null || value === undefined) return '';
  if (type !== 'datetime-local') return String(value);
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return '';
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
